import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import MaterialTypography from "../../components/MaterialTypography";
import MaterialBox from "../../components/MaterialBox";
import MaterialInput from "../../components/MaterialInput";
import MaterialButton from "../../components/MaterialButton";
import DashboardLayout from "../../layoutcontainers/DashboardLayout";
import ConfirmationDialog from "../../components/Dialog/ConfirmationDialog";
import { useLocation, useNavigate } from "react-router-dom";
import {
  createItem,
  deleteItem,
  fetchById,
  updateItem,
} from "../../ApiBaseUrl";
import { useLoading } from "../../context/loading";
import Swal from "sweetalert2";

const FIELDS = [
  "id",
  "name",
  "description",
  "hasPf",
  "hasEsi",
  "hasBankDetails",
  "hasHra",
  "hasShiftPaisa",
  "hasOtAmount",
  "hasAttendanceAmount",
  "hasBusFare",
  "hasMealAmount",
];

const BOOL_FLAGS = [
  { key: "hasPf", label: "PF" },
  { key: "hasEsi", label: "ESI" },
  { key: "hasBankDetails", label: "Bank Details" },
  { key: "hasHra", label: "HRA" },
  { key: "hasShiftPaisa", label: "Shift Paisa" },
  { key: "hasOtAmount", label: "OT Amount" },
  { key: "hasAttendanceAmount", label: "Attendance Amount" },
  { key: "hasBusFare", label: "Bus Fare" },
  { key: "hasMealAmount", label: "Meal Amount" },
];

const initState = {
  name: "",
  description: "",
  hasPf: true,
  hasEsi: true,
  hasBankDetails: true,
  hasHra: true,
  hasShiftPaisa: true,
  hasOtAmount: true,
  hasAttendanceAmount: true,
  hasBusFare: true,
  hasMealAmount: true,
  nameStatus: "unchecked",
};

const AddCategory = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const id = location.state;

  const [togglePage, setTogglePage] = useState({ view: false, edit: false });
  const { setCirleLoading } = useLoading();
  const [categoryData, setCategoryData] = useState({ ...initState });
  const [confirmationDialog, setConfirmationDialog] = useState({
    open: false,
    title: "",
    message: "",
    showCancel: true,
    showConfirm: true,
    customSection: null,
    onConfirm: () => {},
    onCancel: () => {},
  });

  useEffect(() => {
    if (id) {
      fetchCategoryById(id);
      setTogglePage({ view: true, edit: false });
    }
  }, [id]);

  const fetchCategoryById = async (catId) => {
    setCirleLoading(true);
    try {
      const datas = await fetchById("Category", catId, FIELDS);
      const data = datas.getCategoryById;
      setCategoryData({
        name: data.name,
        description: data.description || "",
        hasPf: data.hasPf,
        hasEsi: data.hasEsi,
        hasBankDetails: data.hasBankDetails,
        hasHra: data.hasHra,
        hasShiftPaisa: data.hasShiftPaisa,
        hasOtAmount: data.hasOtAmount,
        hasAttendanceAmount: data.hasAttendanceAmount,
        hasBusFare: data.hasBusFare,
        hasMealAmount: data.hasMealAmount,
        nameStatus: "success",
      });
    } catch (error) {
      console.error("Error fetching category:", error);
    } finally {
      setCirleLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updated = { ...categoryData, [name]: value };
    if (name === "name") {
      const valid = /^.{1,100}$/.test(value.trim());
      updated.nameStatus = value === "" ? "unchecked" : valid ? "success" : "error";
    }
    setCategoryData(updated);
  };

  const handleSwitchChange = (key) => (e) => {
    setCategoryData({ ...categoryData, [key]: e.target.checked });
  };

  const checkAllFields = () => categoryData.nameStatus === "success";

  const buildInput = () => ({
    name: categoryData.name,
    description: categoryData.description,
    hasPf: categoryData.hasPf,
    hasEsi: categoryData.hasEsi,
    hasBankDetails: categoryData.hasBankDetails,
    hasHra: categoryData.hasHra,
    hasShiftPaisa: categoryData.hasShiftPaisa,
    hasOtAmount: categoryData.hasOtAmount,
    hasAttendanceAmount: categoryData.hasAttendanceAmount,
    hasBusFare: categoryData.hasBusFare,
    hasMealAmount: categoryData.hasMealAmount,
  });

  const submitCategory = async () => {
    setCirleLoading(true);
    try {
      if (togglePage.edit) {
        await updateItem("Category", id, buildInput(), FIELDS);
        Swal.fire({ icon: "success", title: "Category updated successfully", timer: 1500, showConfirmButton: false });
      } else {
        await createItem("Category", buildInput(), FIELDS);
        Swal.fire({ icon: "success", title: "Category created successfully", timer: 1500, showConfirmButton: false });
      }
      navigate("/category");
    } catch (error) {
      Swal.fire({ icon: "error", title: "Error", text: error?.message || "Something went wrong" });
    } finally {
      setCirleLoading(false);
    }
  };

  const handleSubmit = () => {
    setConfirmationDialog({
      open: true,
      title: "Confirmation",
      message: "Are you sure you want to submit?",
      showCancel: true,
      showConfirm: true,
      onConfirm: () => {
        submitCategory();
        setConfirmationDialog((prev) => ({ ...prev, open: false }));
      },
      onCancel: () => setConfirmationDialog((prev) => ({ ...prev, open: false })),
    });
  };

  const handleDelete = () => {
    setConfirmationDialog({
      open: true,
      title: "Delete Category",
      message: "Are you sure you want to delete this category? This cannot be undone.",
      showCancel: true,
      showConfirm: true,
      onConfirm: async () => {
        setConfirmationDialog((prev) => ({ ...prev, open: false }));
        setCirleLoading(true);
        try {
          const res = await deleteItem("Category", id);
          if (res?.deleteCategory?.success) {
            Swal.fire({ icon: "success", title: "Category deleted", timer: 1500, showConfirmButton: false });
            navigate("/category");
          } else {
            Swal.fire({ icon: "error", title: "Cannot Delete", text: res?.deleteCategory?.message || "Delete failed" });
          }
        } catch (error) {
          Swal.fire({ icon: "error", title: "Error", text: error?.message || "Something went wrong" });
        } finally {
          setCirleLoading(false);
        }
      },
      onCancel: () => setConfirmationDialog((prev) => ({ ...prev, open: false })),
    });
  };

  const isViewOnly = togglePage.view && !togglePage.edit;

  return (
    <DashboardLayout>
      <Card>
        <CardHeader
          title={
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" alignItems="center" spacing={1}>
                <IconButton onClick={() => navigate("/category")}>
                  <ArrowBackIcon />
                </IconButton>
                <MaterialTypography variant="h5" fontWeight="medium">
                  {id ? (togglePage.edit ? "Edit Category" : "View Category") : "Add Category"}
                </MaterialTypography>
              </Stack>
              {id && (
                <Stack direction="row" spacing={1}>
                  {!togglePage.edit && (
                    <MaterialButton
                      variant="gradient"
                      color="info"
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => setTogglePage({ view: true, edit: true })}
                    >
                      Edit
                    </MaterialButton>
                  )}
                  <MaterialButton
                    variant="gradient"
                    color="error"
                    size="small"
                    startIcon={<DeleteIcon />}
                    onClick={handleDelete}
                  >
                    Delete
                  </MaterialButton>
                </Stack>
              )}
            </Stack>
          }
        />
        <Divider />
        <CardContent>
          <MaterialBox p={2}>
            <Grid container spacing={3}>
              {/* Name */}
              <Grid size={{ xs: 12, md: 6 }}>
                <MaterialTypography variant="caption" fontWeight="bold" textTransform="uppercase">
                  Category Name *
                </MaterialTypography>
                <MaterialInput
                  name="name"
                  value={categoryData.name}
                  onChange={handleChange}
                  disabled={isViewOnly}
                  error={categoryData.nameStatus === "error"}
                  success={categoryData.nameStatus === "success"}
                  placeholder="Enter category name"
                  fullWidth
                />
              </Grid>

              {/* Description */}
              <Grid size={{ xs: 12, md: 6 }}>
                <MaterialTypography variant="caption" fontWeight="bold" textTransform="uppercase">
                  Description
                </MaterialTypography>
                <MaterialInput
                  name="description"
                  value={categoryData.description}
                  onChange={handleChange}
                  disabled={isViewOnly}
                  placeholder="Enter description"
                  fullWidth
                />
              </Grid>

              {/* Boolean Switches */}
              <Grid size={{ xs: 12 }}>
                <Divider sx={{ my: 1 }} />
                <MaterialTypography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>
                  Payroll Component Settings
                </MaterialTypography>
                <Grid container spacing={2}>
                  {BOOL_FLAGS.map(({ key, label }) => (
                    <Grid size={{ xs: 6, sm: 4, md: 3 }} key={key}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={categoryData[key]}
                            onChange={handleSwitchChange(key)}
                            disabled={isViewOnly}
                            color="primary"
                          />
                        }
                        label={
                          <MaterialTypography variant="body2">
                            {label}
                          </MaterialTypography>
                        }
                      />
                    </Grid>
                  ))}
                </Grid>
              </Grid>

              {/* Submit Button */}
              {!isViewOnly && (
                <Grid size={{ xs: 12 }}>
                  <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ mt: 2 }}>
                    <MaterialButton
                      variant="outlined"
                      color="dark"
                      onClick={() => navigate("/category")}
                    >
                      Cancel
                    </MaterialButton>
                    <MaterialButton
                      variant="gradient"
                      color="dark"
                      onClick={handleSubmit}
                      disabled={!checkAllFields()}
                    >
                      {togglePage.edit ? "Update" : "Submit"}
                    </MaterialButton>
                  </Stack>
                </Grid>
              )}
            </Grid>
          </MaterialBox>
        </CardContent>
      </Card>

      <ConfirmationDialog
        open={confirmationDialog.open}
        title={confirmationDialog.title}
        message={confirmationDialog.message}
        showCancel={confirmationDialog.showCancel}
        showConfirm={confirmationDialog.showConfirm}
        onConfirm={confirmationDialog.onConfirm}
        onCancel={confirmationDialog.onCancel}
      />
    </DashboardLayout>
  );
};

export default AddCategory;
